import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import {
  User2, Palette, Receipt, Shield, Mail,
  Sun, Moon, Monitor, Sparkles, Lock
} from 'lucide-react';
import { supabase } from '../supabaseClient';
import { cn, getInitials } from '../lib/utils';
import { Card, CardHeader, CardTitle, CardDescription, CardBody } from './ui/Card';
import { Input, Label } from './ui/Input';
import Button from './ui/Button';
import Tabs from './ui/Tabs';
import PageHeader from './ui/PageHeader';

const TABS = [
  { id: 'profile',      label: 'Profil',         icon: User2 },
  { id: 'appearance',   label: 'Wygląd',         icon: Palette },
  { id: 'billing',      label: 'Plan i kredyty', icon: Receipt },
  { id: 'security',     label: 'Bezpieczeństwo', icon: Shield },
];

export default function SettingsPage({ user, credits, theme, setTheme, clientView = false }) {
  const [tab, setTab] = useState('profile');
  const [fullName, setFullName] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await supabase.from('profiles').select('full_name').eq('id', user.id).single();
      if (data?.full_name) setFullName(data.full_name);
    })();
  }, [user.id]);

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ full_name: fullName })
        .eq('id', user.id);
      if (error) throw error;
      toast.success('Profil zaktualizowany');
    } catch (err) {
      toast.error('Nie udało się zapisać: ' + err.message);
    } finally {
      setSavingProfile(false);
    }
  };

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="Ustawienia"
        subtitle="Profil, wygląd, kredyty i bezpieczeństwo konta."
      />

      <Tabs variant="underline" tabs={clientView ? TABS.filter(t => t.id !== 'billing') : TABS} value={tab} onChange={setTab} className="mb-7" />

      {/* PROFILE */}
      {tab === 'profile' && (
        <div className="space-y-5 max-w-2xl">
          <Card>
            <CardHeader>
              <CardTitle>Twój profil</CardTitle>
              <CardDescription>Tak będziesz widoczny w aplikacji.</CardDescription>
            </CardHeader>
            <CardBody>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-brand-500 to-brand-700 text-white flex items-center justify-center text-xl font-bold shadow-md">
                  {getInitials(user.email)}
                </div>
                <div>
                  <div className="font-semibold text-ink-900 dark:text-ink-50">{user.email}</div>
                  <div className="text-xs text-ink-500 dark:text-ink-400 mt-0.5">Użytkownik · od {new Date(user.created_at).toLocaleDateString('pl-PL')}</div>
                </div>
              </div>

              <form onSubmit={handleSaveProfile} className="space-y-4">
                <div>
                  <Label htmlFor="fullname">Imię i nazwisko</Label>
                  <Input
                    id="fullname"
                    value={fullName}
                    onChange={e => setFullName(e.target.value)}
                    placeholder="Jan Kowalski"
                    leftIcon={<User2 className="w-4 h-4" />}
                  />
                </div>
                <div>
                  <Label htmlFor="email-readonly">Adres e-mail</Label>
                  <Input
                    id="email-readonly"
                    type="email"
                    value={user.email}
                    disabled
                    leftIcon={<Mail className="w-4 h-4" />}
                  />
                  <div className="text-2xs text-ink-500 dark:text-ink-400 mt-1.5">Aby zmienić e-mail, skontaktuj się z supportem.</div>
                </div>
                <Button type="submit" loading={savingProfile} className="mt-2">
                  {savingProfile ? 'Zapisywanie…' : 'Zapisz zmiany'}
                </Button>
              </form>
            </CardBody>
          </Card>
        </div>
      )}

      {/* APPEARANCE */}
      {tab === 'appearance' && (
        <div className="space-y-5 max-w-2xl">
          <Card>
            <CardHeader>
              <CardTitle>Motyw</CardTitle>
              <CardDescription>Wybierz wygląd interfejsu — jasny, ciemny, lub automatyczny.</CardDescription>
            </CardHeader>
            <CardBody>
              <div className="grid grid-cols-3 gap-3">
                <ThemeOption
                  active={theme === 'light'}
                  onClick={() => setTheme('light')}
                  icon={Sun}
                  label="Jasny"
                  preview="light"
                />
                <ThemeOption
                  active={theme === 'dark'}
                  onClick={() => setTheme('dark')}
                  icon={Moon}
                  label="Ciemny"
                  preview="dark"
                />
                <ThemeOption
                  active={theme === 'system'}
                  onClick={() => setTheme('system')}
                  icon={Monitor}
                  label="System"
                  preview="auto"
                />
              </div>
            </CardBody>
          </Card>
        </div>
      )}

      {/* BILLING */}
      {tab === 'billing' && (
        <div className="space-y-5 max-w-2xl">
          {/* T2.3 (audyt): konto klienta agencji nie widzi kredytów ani planu. */}
          {!clientView && <Card>
            <CardHeader>
              <CardTitle>Aktualny plan</CardTitle>
              <CardDescription>Twoje saldo kredytów i bieżący abonament.</CardDescription>
            </CardHeader>
            <CardBody>
              <div className="flex items-center justify-between p-4 rounded-lg bg-gradient-to-br from-brand-50 to-brand-100 dark:from-brand-900/30 dark:to-brand-800/20 border border-brand-200 dark:border-brand-800/50">
                <div>
                  <div className="text-2xs uppercase font-bold tracking-wide text-brand-700/80 dark:text-brand-300/80">Saldo</div>
                  <div className="text-4xl font-bold tracking-tighter mt-1 leading-none bg-gradient-to-br from-brand-700 to-brand-950 bg-clip-text text-transparent dark:from-brand-200 dark:to-brand-400">
                    {credits === Infinity ? 'bez limitu' : (credits ?? 0).toLocaleString('pl-PL')}
                  </div>
                  <div className="text-xs text-brand-700/70 dark:text-brand-300/70 mt-1">kredytów dostępnych</div>
                </div>
                <a href="mailto:kontakt@mediafy.com.pl?subject=MAMS%20%E2%80%94%20do%C5%82adowanie%20kredyt%C3%B3w">
                  <Button leftIcon={<Sparkles className="w-4 h-4" />} variant="primary">
                    Doładuj kredyty
                  </Button>
                </a>
              </div>

              <p className="mt-4 text-xs text-ink-500 dark:text-ink-400">
                Doładowania i faktury rozliczamy indywidualnie — napisz na kontakt@mediafy.com.pl, odpowiadamy w ciągu dnia roboczego.
              </p>
            </CardBody>
          </Card>}
        </div>
      )}

      {/* SECURITY */}
      {tab === 'security' && (
        <div className="space-y-5 max-w-2xl">
          <Card>
            <CardHeader>
              <CardTitle>Hasło</CardTitle>
              <CardDescription>Zmień hasło dostępu do konta.</CardDescription>
            </CardHeader>
            <CardBody>
              <Button
                variant="secondary"
                leftIcon={<Lock className="w-4 h-4" />}
                onClick={async () => {
                  await supabase.auth.resetPasswordForEmail(user.email);
                  toast.success(`Link resetu wysłany na ${user.email}`);
                }}
              >
                Wyślij link do zmiany hasła
              </Button>
            </CardBody>
          </Card>

        </div>
      )}
    </div>
  );
}

function ThemeOption({ active, onClick, icon: Icon, label, preview }) {
  const previewBg = preview === 'light'
    ? 'bg-gradient-to-br from-white to-ink-50 border-ink-200'
    : preview === 'dark'
    ? 'bg-gradient-to-br from-ink-800 to-ink-950 border-ink-700'
    : 'bg-gradient-to-br from-ink-100 to-ink-300 border-ink-300';
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'group relative rounded-xl p-3 border-2 transition-all',
        active
          ? 'border-brand-600 shadow-md shadow-brand-600/10'
          : 'border-ink-200 hover:border-ink-300 dark:border-ink-700 dark:hover:border-ink-600'
      )}
    >
      <div className={cn('h-20 rounded-md border mb-3 relative overflow-hidden', previewBg)}>
        <div className={cn(
          'absolute top-2 left-2 w-6 h-2 rounded',
          preview === 'dark' ? 'bg-ink-700' : 'bg-ink-200'
        )} />
        <div className={cn(
          'absolute bottom-2 left-2 right-2 h-2 rounded',
          preview === 'dark' ? 'bg-brand-600' : 'bg-brand-500'
        )} />
      </div>
      <div className="flex items-center justify-center gap-2">
        <Icon className={cn('w-4 h-4', active ? 'text-brand-600 dark:text-brand-400' : 'text-ink-500 dark:text-ink-400')} />
        <span className={cn(
          'text-sm font-semibold',
          active ? 'text-brand-700 dark:text-brand-300' : 'text-ink-700 dark:text-ink-300'
        )}>
          {label}
        </span>
      </div>
      {active && (
        <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-brand-600 text-white flex items-center justify-center text-xs">
          ✓
        </div>
      )}
    </button>
  );
}

